import { fetchWeatherApi } from 'openmeteo';
import { Dayjs } from 'dayjs';
import { ensureDayjs } from './dateUtils';

export interface WeatherData {
    hourly: {
        time: Date[];
        windSpeed10m: Float32Array | null;
        windSpeed80m: Float32Array | null;
        windSpeed120m: Float32Array | null;
        windSpeed180m: Float32Array | null;
        windDirection10m: Float32Array | null;
        windDirection80m: Float32Array | null;
        windDirection120m: Float32Array | null;
        windDirection180m: Float32Array | null;
        windspeed975hPa: Float32Array | null;
        windspeed950hPa: Float32Array | null;
        windspeed925hPa: Float32Array | null;
        windspeed900hPa: Float32Array | null;
        windspeed850hPa: Float32Array | null;
        windspeed800hPa: Float32Array | null;
        windspeed700hPa: Float32Array | null;
        windspeed600hPa: Float32Array | null;
        winddirection975hPa: Float32Array | null;
        winddirection950hPa: Float32Array | null;
        winddirection925hPa: Float32Array | null;
        winddirection900hPa: Float32Array | null;
        winddirection850hPa: Float32Array | null;
        winddirection800hPa: Float32Array | null;
        winddirection700hPa: Float32Array | null;
        winddirection600hPa: Float32Array | null;
    };
}

export function openmeteoFetch(lat: number = 52.52, lng: number = 13.41, forecast_time: Dayjs | string | Date | number) {
    // Validate inputs
    if (typeof lat !== 'number' || typeof lng !== 'number') {
        throw new Error('Valid numeric coordinates required');
    }
    
    // Convert forecast_time to Dayjs using helper function
    const dayjsTime = ensureDayjs(forecast_time);
    
    let utc_time;
    try {
        utc_time = dayjsTime.utc();
    } catch (e) {
        throw new Error(`Failed to convert date to UTC: ${(e as Error).message}`);
    }
    
    const format = "YYYY-MM-DDTHH:00";
    let start_time, end_time;
    try {
        start_time = utc_time.format(format);
        end_time = utc_time.add(1, 'hour').format(format);
    } catch (e) {
        throw new Error(`Failed to format dates: ${(e as Error).message}`);
    }
    
    const models = ["best_match", "gfs_global", "icon_global", "ecmwf_ifs025"]
    const params = {
        "latitude": lat,
        "longitude": lng,
        "hourly": ["wind_speed_10m", "wind_speed_80m", "wind_speed_120m", "wind_speed_180m", "wind_direction_10m", "wind_direction_80m", "wind_direction_120m", "wind_direction_180m", "windspeed_975hPa", "windspeed_950hPa", "windspeed_925hPa", "windspeed_900hPa", "windspeed_850hPa", "windspeed_800hPa", "windspeed_700hPa", "windspeed_600hPa", "winddirection_975hPa", "winddirection_950hPa", "winddirection_925hPa", "winddirection_900hPa", "winddirection_850hPa", "winddirection_800hPa", "winddirection_700hPa", "winddirection_600hPa"],
        "wind_speed_unit": "ms",
        "start_hour": start_time,
        "end_hour": end_time,
        "models": models
    };

    const url = "https://api.open-meteo.com/v1/forecast";
    
    // Add timeout to prevent hanging requests
    const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Weather API request timeout')), 10000); // 10 second timeout
    });
    
    return Promise.race([
        fetchWeatherApi(url, params),
        timeoutPromise
    ]).then((responses) => {

        // Helper function to form time ranges
        const range = (start: number, stop: number, step: number) =>
            Array.from({ length: (stop - start) / step }, (_, i) => start + i * step);

        let out = {}
        // Process first location. Add a for-loop for multiple locations or weather models
        responses.forEach(function (response, response_idx) {

            // Attributes for timezone and location
            const utcOffsetSeconds = response.utcOffsetSeconds();
            const timezone = response.timezone();
            const timezoneAbbreviation = response.timezoneAbbreviation();
            const latitude = response.latitude();
            const longitude = response.longitude();

            const hourly = response.hourly();

            // Note: The order of weather variables in the URL query and the indices below need to match
            const weatherData: WeatherData = {
                hourly: {
                    time: range(Number(hourly.time()), Number(hourly.timeEnd()), hourly.interval()).map(
                        (t) => new Date((t + utcOffsetSeconds) * 1000)
                    ),
                    windSpeed10m: hourly.variables(0).valuesArray(),
                    windSpeed80m: hourly.variables(1).valuesArray(),
                    windSpeed120m: hourly.variables(2).valuesArray(),
                    windSpeed180m: hourly.variables(3).valuesArray(),
                    windDirection10m: hourly.variables(4).valuesArray(),
                    windDirection80m: hourly.variables(5).valuesArray(),
                    windDirection120m: hourly.variables(6).valuesArray(),
                    windDirection180m: hourly.variables(7).valuesArray(),
                    windspeed975hPa: hourly.variables(8).valuesArray(),
                    windspeed950hPa: hourly.variables(9).valuesArray(),
                    windspeed925hPa: hourly.variables(10).valuesArray(),
                    windspeed900hPa: hourly.variables(11).valuesArray(),
                    windspeed850hPa: hourly.variables(12).valuesArray(),
                    windspeed800hPa: hourly.variables(13).valuesArray(),
                    windspeed700hPa: hourly.variables(14).valuesArray(),
                    windspeed600hPa: hourly.variables(15).valuesArray(),
                    winddirection975hPa: hourly.variables(16).valuesArray(),
                    winddirection950hPa: hourly.variables(17).valuesArray(),
                    winddirection925hPa: hourly.variables(18).valuesArray(),
                    winddirection900hPa: hourly.variables(19).valuesArray(),
                    winddirection850hPa: hourly.variables(20).valuesArray(),
                    winddirection800hPa: hourly.variables(21).valuesArray(),
                    winddirection700hPa: hourly.variables(22).valuesArray(),
                    winddirection600hPa: hourly.variables(23).valuesArray(),
                },

            };

            out[models[response_idx]] = weatherData;
        });

        return out;
    }).catch(error => {
        console.error('Weather fetch error:', error);
        throw error;
    });
}
